import { useGetMaintenanceQuery } from 'app/services/tools';
import { Tool, ToolMaintenance } from 'app/types/tool';
import { Column, ColumnTypes, DataTable } from 'components';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { globalActions } from 'app/services/global';
import { Drawers } from 'app/types/drawer';
import { ToolCleaningType, ToolFilterState } from 'constants/tool';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { User } from 'constants/user';
import DeleteMaintenance from './DeleteButton';

type MaintenanceTableProps = {
  tool?: Tool;
  // Pre-fetched maintenance history (e.g. from the public tool overview endpoint) - when
  // provided, the authenticated per-tool query below is skipped entirely.
  data?: ToolMaintenance[];
  // Hides the edit/delete action column - used by the public, unauthenticated
  // /tools/overview/:barcode page. Row click still opens the readonly view drawer.
  readonly?: boolean;
};

const MaintenanceTable = ({ tool, data: dataOverride, readonly }: MaintenanceTableProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasManageMaintenancePermission = useHasPermission(User.Permissions.MANAGE_TOOL_MAINTENANCE);
  const canManageMaintenance = !readonly && hasManageMaintenancePermission;

  const { data: fetchedData, isLoading, isFetching } = useGetMaintenanceQuery(tool?.Id ?? 0, { skip: !tool || !!dataOverride });
  const data = dataOverride ?? fetchedData;

  const handleClick = (maintenance: ToolMaintenance) => {
    dispatch(globalActions.openDrawer(
      {
        key: Drawers.EDIT_MAINTENANCE,
        props: { maintenance: maintenance }
      }));
  };

  const handleViewClick = (maintenance: ToolMaintenance) => {
    dispatch(globalActions.openDrawer(
      {
        key: Drawers.VIEW_MAINTENANCE,
        props: { maintenance: maintenance },
        readonly: true,
      }));
  };

  const columns: Column[] = [
    {
      key: 'ExecutedAt',
      title: t('entities.maintenance.executedat'),
      type: ColumnTypes.DATE,
      defaultOrder: 'descend',
    },
    {
      key: 'CleaningType',
      title: t('entities.maintenance.cleaningtype'),
      type: ColumnTypes.TAG,
      tags: [
        { key: 'CleaningType', value: ToolCleaningType.Wet, color: 'blue', message: t('entities.maintenance.wet') },
        { key: 'CleaningType', value: ToolCleaningType.Dry, color: 'default', message: t('entities.maintenance.dry') },
        { key: 'CleaningType', value: ToolCleaningType.WetAndDry, color: 'blue', message: t('entities.maintenance.wet') },
        { key: 'CleaningType', value: ToolCleaningType.WetAndDry, color: 'default', message: t('entities.maintenance.dry') },
        { key: 'CleaningType', value: ToolCleaningType.NoInfo, color: 'warning', message: t('entities.maintenance.noinfo') },
      ],
    },
    {
      key: 'FilterState',
      title: t('entities.maintenance.filterstate'),
      type: ColumnTypes.TAG,
      tags: [
        { key: 'FilterState', value: ToolFilterState.New, color: 'success', message: t('entities.maintenance.filterstates.new') },
        { key: 'FilterState', value: ToolFilterState.Old, color: 'warning', message: t('entities.maintenance.filterstates.old') },
        { key: 'FilterState', value: ToolFilterState.None, color: 'default', message: t('entities.maintenance.filterstates.none') },
      ],
    },
    {
      key: 'IsElectricalCheckDone',
      title: t('entities.maintenance.iselectricalcheckdone'),
      type: ColumnTypes.TAG,
      tags: [
        { key: 'IsElectricalCheckDone', value: true, color: 'success', message: t('common.yes') },
        { key: 'IsElectricalCheckDone', value: false, color: 'default', message: t('common.no') },
      ],
    },
    {
      key: 'IsMechanicalCheckDone',
      title: t('entities.maintenance.ismechanicalcheckdone'),
      type: ColumnTypes.TAG,
      tags: [
        { key: 'IsMechanicalCheckDone', value: true, color: 'success', message: t('common.yes') },
        { key: 'IsMechanicalCheckDone', value: false, color: 'default', message: t('common.no') },
      ],
    },
    {
      key: 'IsWorking',
      title: t('entities.maintenance.isworking'),
      type: ColumnTypes.TAG,
      tags: [
        { key: 'IsWorking', value: true, color: 'success', message: t('common.yes') },
        { key: 'IsWorking', value: false, color: 'error', message: t('common.no') },
      ],
    },
    {
      key: 'Comments',
      title: t('entities.maintenance.comments'),
      type: ColumnTypes.TEXT,
    },
    {
      key: 'ExecutorName',
      title: t('entities.maintenance.executorname'),
      type: ColumnTypes.CUSTOM,
      renderAs: (maintenance: ToolMaintenance) => (
        <>
          <div>{maintenance.ExecutorName}</div>
          {maintenance.CompanyName && <div>{maintenance.CompanyName}</div>}
        </>
      ),
    },
  ];

  if (canManageMaintenance) {
    columns.push(
      {
        key: 'Action',
        title: t('common.action'),
        type: ColumnTypes.ACTIONS,
        actions: [
          {
            icon: <EditOutlined />,
            toolTip: t('common.editbytype', { type: t('entities.maintenance.title').toLowerCase() }),
            onClick: handleClick
          },
          (maintenance) => <DeleteMaintenance maintenance={maintenance} />
        ],
      },
    );}

  return <DataTable
    data={data || []}
    columns={columns}
    isLoading={isLoading || isFetching}
    pagination={true}
    tableKey="ToolMaintenance"
    handleRowClick={handleViewClick}
  />;
};

export default MaintenanceTable;

